import * as React from 'react';
import {
  Star,
  Clock,
  Search,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar, ScrollAreaThumb } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import type { CostCentre } from '@/types/timesheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface CostCentreWithMetadata extends CostCentre {
  lastAccessedDate?: Date;
}

interface CostCentreSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  costCentres: CostCentreWithMetadata[];
  value?: string;
  onSelect: (costCentreId: string) => void;
  onToggleFavourite: (costCentreId: string) => void;
  isFavourite: (costCentreId: string) => boolean;
  /** When false, hides the Favourites section and star toggle buttons (e.g. approver screens) */
  showFavourites?: boolean;
  // Server-side pagination props (optional - for future use)
  onLoadMore?: () => void;
  hasMore?: boolean;
  remainingCount?: number;
  isLoadingMore?: boolean;
  // Server-side search props (optional - for future use)
  onServerSearch?: (query: string) => void;
  isSearching?: boolean;
  totalCount?: number;
}

const ITEMS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export function CostCentreSearchModal({
  open,
  onOpenChange,
  costCentres,
  value,
  onSelect,
  onToggleFavourite,
  isFavourite,
  showFavourites = true,
  // Server-side pagination props (optional - for future use)
  onLoadMore,
  hasMore: serverHasMore,
  remainingCount,
  isLoadingMore,
  // Server-side search props (optional - for future use)
  onServerSearch,
  isSearching,
  totalCount,
}: CostCentreSearchModalProps) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [allCostCentresPage, setAllCostCentresPage] = React.useState(1);
  const [favouritesExpanded, setFavouritesExpanded] = React.useState(true);
  const [recentExpanded, setRecentExpanded] = React.useState(true);
  const [allCostCentresExpanded, setAllCostCentresExpanded] = React.useState(true);

  // Debounce search input and trigger server-side search if available
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      // Trigger server-side search if callback is provided
      if (onServerSearch) {
        onServerSearch(searchQuery);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery, onServerSearch]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setDebouncedSearch('');
      setAllCostCentresPage(1);
    }
  }, [open]);

  // Fuzzy search implementation
  const fuzzyMatch = React.useCallback((text: string, query: string): boolean => {
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase();
    
    // Exact match
    if (textLower.includes(queryLower)) return true;
    
    // Fuzzy match - allow for typos
    let queryIndex = 0;
    for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
      if (textLower[i] === queryLower[queryIndex]) {
        queryIndex++;
      }
    }
    return queryIndex === queryLower.length;
  }, []);

  // Filter and search cost centres
  const filteredCostCentres = React.useMemo(() => {
    let filtered = costCentres;

    // Apply search
    if (debouncedSearch) {
      filtered = filtered.filter((cc) => {
        return (
          fuzzyMatch(cc.name, debouncedSearch) ||
          fuzzyMatch(cc.code, debouncedSearch)
        );
      });
    }

    return filtered;
  }, [costCentres, debouncedSearch, fuzzyMatch]);

  // Categorize filtered cost centres
  const categorizedCostCentres = React.useMemo(() => {
    const favourites = filteredCostCentres
      .filter((cc) => isFavourite(cc.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    const recent = filteredCostCentres
      .filter((cc) => cc.lastAccessedDate && !isFavourite(cc.id))
      .sort((a, b) => {
        const aTime = a.lastAccessedDate?.getTime() || 0;
        const bTime = b.lastAccessedDate?.getTime() || 0;
        return bTime - aTime;
      })
      .slice(0, 10);

    const recentIds = new Set(recent.map((cc) => cc.id));
    const favouriteIds = new Set(favourites.map((cc) => cc.id));

    const all = filteredCostCentres
      .filter((cc) => !favouriteIds.has(cc.id) && !recentIds.has(cc.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { favourites, recent, all };
  }, [filteredCostCentres, isFavourite]);

  // Paginate all cost centres
  const paginatedAllCostCentres = React.useMemo(() => {
    return categorizedCostCentres.all.slice(0, allCostCentresPage * ITEMS_PER_PAGE);
  }, [categorizedCostCentres.all, allCostCentresPage]);

  const hasMoreCostCentres = paginatedAllCostCentres.length < categorizedCostCentres.all.length;

  // Handle star toggle with animation
  const handleToggleFavourite = React.useCallback(
    (e: React.MouseEvent, costCentreId: string) => {
      e.stopPropagation();
      onToggleFavourite(costCentreId);
      
      // Announce to screen readers
      const costCentre = costCentres.find((cc) => cc.id === costCentreId);
      const message = isFavourite(costCentreId)
        ? `${costCentre?.name} removed from favourites`
        : `${costCentre?.name} added to favourites`;
      
      // Create a temporary announcement element
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = message;
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 1000);
    },
    [onToggleFavourite, isFavourite, costCentres]
  );

  // Handle cost centre selection
  const handleSelect = React.useCallback(
    (costCentreId: string) => {
      onSelect(costCentreId);
      onOpenChange(false);
    },
    [onSelect, onOpenChange]
  );

  // Render cost centre item
  const renderCostCentreItem = React.useCallback(
    (costCentre: CostCentreWithMetadata) => (
      <button
        key={costCentre.id}
        onClick={() => handleSelect(costCentre.id)}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-accent transition-colors text-left group',
          value === costCentre.id && 'bg-accent'
        )}
      >
        {/* Star toggle — hidden when showFavourites is false */}
        {showFavourites && (
          <span
            role="button"
            onClick={(e) => handleToggleFavourite(e, costCentre.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleFavourite(e as unknown as React.MouseEvent, costCentre.id);
              }
            }}
            className="shrink-0 p-0.5 rounded hover:bg-accent-foreground/10 transition-colors cursor-pointer"
            aria-label={isFavourite(costCentre.id) ? 'Remove from favourites' : 'Add to favourites'}
            tabIndex={-1}
          >
            <Star
              className={cn(
                'h-4 w-4 transition-all',
                isFavourite(costCentre.id)
                  ? 'fill-warning-400 text-warning-400'
                  : 'text-muted-foreground group-hover:text-warning-400'
              )}
            />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-xs text-muted-foreground">{costCentre.code}</span>
            <span className="truncate">{costCentre.name}</span>
          </div>
        </div>
      </button>
    ),
    [value, handleSelect, handleToggleFavourite, isFavourite, showFavourites]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Select Cost Centre</DialogTitle>
        </DialogHeader>

        {/* Search Input */}
        <div className="px-6 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <Separator />

        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="max-h-[50vh] px-6 py-4">
            {/* Favourites Section */}
            {showFavourites && categorizedCostCentres.favourites.length > 0 && (
              <Collapsible
                open={favouritesExpanded}
                onOpenChange={setFavouritesExpanded}
                className="mb-6"
              >
                <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
                  {favouritesExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  <Star className="h-4 w-4" />
                  <span>FAVOURITES ({categorizedCostCentres.favourites.length})</span>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1 mt-2">
                  {categorizedCostCentres.favourites.map(renderCostCentreItem)}
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Recently Used Section */}
            {categorizedCostCentres.recent.length > 0 && (
              <Collapsible
                open={recentExpanded}
                onOpenChange={setRecentExpanded}
                className="mb-6"
              >
                <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
                  {recentExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  <Clock className="h-4 w-4" />
                  <span>RECENTLY USED ({categorizedCostCentres.recent.length})</span>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1 mt-2">
                  {categorizedCostCentres.recent.map(renderCostCentreItem)}
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* All Cost Centres Section */}
            {categorizedCostCentres.all.length > 0 && (
              <Collapsible
                open={allCostCentresExpanded}
                onOpenChange={setAllCostCentresExpanded}
                className="mb-6"
              >
                <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
                  {allCostCentresExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  <Search className="h-4 w-4" />
                  <span>
                    {debouncedSearch ? 'SEARCH RESULTS' : 'ALL COST CENTRES'} (
                    {categorizedCostCentres.all.length})
                  </span>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1 mt-2">
                  {paginatedAllCostCentres.map(renderCostCentreItem)}
                  {/* Server-side pagination: use onLoadMore if provided */}
                  {onLoadMore && serverHasMore && (
                    <Button
                      variant="ghost"
                      className="w-full mt-2"
                      onClick={onLoadMore}
                      disabled={isLoadingMore}
                    >
                      {isLoadingMore ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        `Load More (${remainingCount ?? '?'} remaining)`
                      )}
                    </Button>
                  )}
                  {/* Client-side pagination: fallback when no server-side pagination */}
                  {!onLoadMore && hasMoreCostCentres && (
                    <Button
                      variant="ghost"
                      className="w-full mt-2"
                      onClick={() => setAllCostCentresPage((prev) => prev + 1)}
                    >
                      Load More ({categorizedCostCentres.all.length - paginatedAllCostCentres.length} remaining)
                    </Button>
                  )}
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* No Results Message */}
            {filteredCostCentres.length === 0 && (
              <div className="py-12 text-center text-muted-foreground">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">No cost centres found</p>
                {debouncedSearch && (
                  <p className="text-xs mt-1">Try a different search term</p>
                )}
              </div>
            )}
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical">
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
